import type { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form@latest';
import { ErrorMessage } from '@hookform/error-message';
import { createSelector } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';

import { _isNotBlank } from '@/littledash';

import { featuresSelector } from '@/support/Selectors';

import ReasonForChange from '@/components/Shared/Latest/ReasonForChange';

interface ConfirmDeleteProps {
  onClick: (reason?: string) => void;
  title: string;
  text: string;
  btnTxt: string;
  textOverride?: string;
  showReasonForChange?: boolean;
}

interface FormDataProps {
  confirm: string;
  reason_for_change: string;
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  reasonForChange: features?.reason_for_change ?? false,
}));

const ConfirmDelete: FC<ConfirmDeleteProps> = ({ onClick, title, text, btnTxt, textOverride, showReasonForChange }) => {
  const { reasonForChange } = useSelector(featureSelector);
  const dispatch = useDispatch();

  const formMethods = useForm<FormDataProps>({
    defaultValues: {
      confirm: '',
      reason_for_change: '',
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = formMethods;

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const onSubmit = ({ reason_for_change }: FormDataProps) => {
    const shouldPassReason = reasonForChange === true && showReasonForChange === true;
    onClick(shouldPassReason ? reason_for_change : undefined);
    closeModal();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} className="ui__narrow-modal" data-testid="confirm-delete__modal">
        <div className="pb3 tc">
          <h3 className="normal f4 pb3" data-testid="confirm-delete__modal-header">
            {title}
          </h3>
          <p className="f6 pb2" data-testid="confirm-delete__modal-subheader">
            {_isNotBlank(textOverride) ? textOverride : text}
          </p>
        </div>
        <div className="pb3">
          {reasonForChange === true && showReasonForChange === true ? (
            <ReasonForChange data-testid="confirm-delete__modal-reason-for-change" isDelete />
          ) : (
            <>
              <label htmlFor="confirm" className="lh-copy" data-testid="confirm-delete__modal-label">
                Type DELETE into the field to confirm
              </label>
              <input
                type="text"
                id="confirm"
                data-testid="confirm-delete__modal-input"
                {...register('confirm', { validate: (value: string) => value === 'DELETE' })}
              />
              <ErrorMessage
                errors={errors}
                name="confirm"
                render={() => (
                  <p className="f6 red db lh-copy" data-testid="confirm-delete__modal-validation">
                    Text entered is incorrect, enter DELETE to continue.
                  </p>
                )}
              />
            </>
          )}
        </div>
        <div>
          <button type="submit" className="button critical w-100" data-testid="confirm-delete__modal-confirm">
            {btnTxt}
          </button>
          <button
            type="button"
            className="button plain db w-100 mt2"
            onClick={closeModal}
            data-testid="confirm-delete__modal-cancel"
          >
            Cancel
          </button>
        </div>
      </form>
    </FormProvider>
  );
};

export default ConfirmDelete;
